import {
	AutoRouteResult as SysApiAutoRouteResult,
	CharacterizationMeasurementResult as SysApiCharacterizationMeasurementResult,
	CharacterizationReportResult as SysApiCharacterizationReportResult,
	RoomAdaptStatus as SysApiRoomAdaptStatus,
	SignalContinuityResult as SysApiSignalContinuityResult
} from '@shure/shared/angular/data-access/system-api/core';

import {
	AutoRouteResult,
	CharacterizationMeasurementResult,
	CharacterizationReportResult,
	RoomAdaptDevice,
	RoomAdaptRoomDevice,
	RoomAdaptStatus,
	RoomCharacterizationSummary,
	SignalContinuityResult
} from '../../api/models';
import { RoomAdaptDeviceFragment, RoomAdaptRoomDevicesFragment } from '../graphql/generated/room-adapt.generated';

export function mapRoomAdaptStatusFromSysApi(status: SysApiRoomAdaptStatus | null | undefined): RoomAdaptStatus {
	switch (status) {
		case SysApiRoomAdaptStatus.Idle:
			return RoomAdaptStatus.Idle;
		case SysApiRoomAdaptStatus.AutoRouteAssociating:
			return RoomAdaptStatus.AutoRouteAssociating;
		case SysApiRoomAdaptStatus.AutoRouteOptimizing:
			return RoomAdaptStatus.AutoRouteOptimizing;
		case SysApiRoomAdaptStatus.RoomCharacterizationPrompting:
			return RoomAdaptStatus.RoomCharacterizationPrompting;
		case SysApiRoomAdaptStatus.ContinuityCheck:
			return RoomAdaptStatus.ContinuityCheck;
		case SysApiRoomAdaptStatus.RoomCharacterizationInProgress:
			return RoomAdaptStatus.RoomCharacterizationInProgress;
		case SysApiRoomAdaptStatus.GeneratingReport:
			return RoomAdaptStatus.GeneratingReport;
		default:
			return RoomAdaptStatus.Idle;
	}
}

export function mapRoomAdaptDeviceFromSysApi(fragment: RoomAdaptDeviceFragment): RoomAdaptDevice {
	return {
		id: fragment.id,
		name: fragment.features.name?.name ?? '',
		roomId: fragment.features.room?.room?.id ?? null,
		features: {
			roomCharacterization: fragment.description.features.roomCharacterization?.supported
				? {
						readiness: fragment.features.roomCharacterization?.readiness ?? false,
						reportDownloadAvailable:
							fragment.features.roomCharacterization?.reportDownloadAvailable ?? false,
						summary: fragment.features.roomCharacterization?.summary
							? mapRoomCharacterizationSummary(fragment.features.roomCharacterization.summary)
							: null,
						isMissing: !fragment.features.roomCharacterization
					}
				: undefined,
			roomAdapt: fragment.description.features.roomAdapt?.supported
				? {
						status: mapRoomAdaptStatusFromSysApi(fragment.features.roomAdapt?.status),
						isMissing: !fragment.features.roomAdapt
					}
				: undefined
		}
	};
}

export function mapRoomAdaptRoomDevicesFromSysApi(fragment: RoomAdaptRoomDevicesFragment): RoomAdaptRoomDevice[] {
	return fragment.devices.map((device) => ({
		id: device.id,
		roomAdaptSupported: device.description.features.roomCharacterization?.supported ?? false
	}));
}

function mapRoomCharacterizationSummary(
	summary: NonNullable<NonNullable<RoomAdaptDeviceFragment['features']['roomCharacterization']>['summary']>
): RoomCharacterizationSummary {
	return {
		ambientNoise: summary.ambientNoise,
		coupling: summary.coupling,
		distortion: summary.distortion,
		reverberation: summary.reverberation,
		signalContinuityResult: summary.signalContinuityResult
			? mapSignalContinuityResult(summary.signalContinuityResult)
			: null,
		autoRouteResult: summary.autoRouteResult ? mapAutoRouteResult(summary.autoRouteResult) : null,
		characterizationMeasurementResult: summary.characterizationMeasurementResult
			? mapCharacterizationMeasurementResult(summary.characterizationMeasurementResult)
			: null,
		characterizationReportResult: summary.characterizationReportResult
			? mapCharacterizationReportResult(summary.characterizationReportResult)
			: null
	};
}

function mapSignalContinuityResult(
	result: SysApiSignalContinuityResult | `${SysApiSignalContinuityResult}`
): SignalContinuityResult {
	switch (result) {
		case SysApiSignalContinuityResult.Full:
			return SignalContinuityResult.Full;
		case SysApiSignalContinuityResult.Partial:
			return SignalContinuityResult.Partial;
		case SysApiSignalContinuityResult.None:
			return SignalContinuityResult.None;
		case SysApiSignalContinuityResult.Failed:
			return SignalContinuityResult.Failed;
		case SysApiSignalContinuityResult.Aborted:
			return SignalContinuityResult.Aborted;
		case SysApiSignalContinuityResult.NotAvailable:
			return SignalContinuityResult.NotAvailable;
		default:
			return SignalContinuityResult.NotAvailable;
	}
}

function mapAutoRouteResult(result: SysApiAutoRouteResult | `${SysApiAutoRouteResult}`): AutoRouteResult {
	switch (result) {
		case SysApiAutoRouteResult.Successful:
			return AutoRouteResult.Successful;
		case SysApiAutoRouteResult.PartiallySuccessful:
			return AutoRouteResult.PartiallySuccessful;
		case SysApiAutoRouteResult.Failed:
			return AutoRouteResult.Failed;
		case SysApiAutoRouteResult.Aborted:
			return AutoRouteResult.Aborted;
		case SysApiAutoRouteResult.Rejected:
			return AutoRouteResult.Rejected;
		default:
			return AutoRouteResult.Failed;
	}
}

function mapCharacterizationReportResult(
	result: SysApiCharacterizationReportResult | `${SysApiCharacterizationReportResult}`
): CharacterizationReportResult {
	switch (result) {
		case SysApiCharacterizationReportResult.Successful:
			return CharacterizationReportResult.Successful;
		case SysApiCharacterizationReportResult.Failed:
			return CharacterizationReportResult.Failed;
		case SysApiCharacterizationReportResult.Aborted:
			return CharacterizationReportResult.Aborted;
		case SysApiCharacterizationReportResult.NotAvailable:
			return CharacterizationReportResult.NotAvailable;
		default:
			return CharacterizationReportResult.Failed;
	}
}

function mapCharacterizationMeasurementResult(
	result: SysApiCharacterizationMeasurementResult | `${SysApiCharacterizationMeasurementResult}`
): CharacterizationMeasurementResult {
	switch (result) {
		case SysApiCharacterizationMeasurementResult.Successful:
			return CharacterizationMeasurementResult.Successful;
		case SysApiCharacterizationMeasurementResult.Failed:
			return CharacterizationMeasurementResult.Failed;
		case SysApiCharacterizationMeasurementResult.Aborted:
			return CharacterizationMeasurementResult.Aborted;
		case SysApiCharacterizationMeasurementResult.NotAvailable:
			return CharacterizationMeasurementResult.NotAvailable;
		default:
			return CharacterizationMeasurementResult.NotAvailable;
	}
}
